import { loginBySharedAuth } from '../api/auth-api'
import { commitAuthSession } from './auth-actions'
import { redirectToLogin } from './auth-navigation'
import type { AuthApiLoginData } from './auth-session'
import { resolveAuthMode } from './auth-session'
import { readStoredSession } from './auth-storage'
import { authStore, clearAuthSession } from './auth-store'

const SHARED_AUTH_COOKIE_KEY = 'ql_shared_auth'
const SHARED_LOGOUT_COOKIE_KEY = 'ql_logout_at'
const SHARED_COOKIE_DOMAIN = '.qianlu.net'
const SHARED_COOKIE_PATH = '/'
const SHARED_COOKIE_ROOT_HOST = 'qianlu.net'
const SHARED_LOGOUT_COOKIE_MAX_AGE = 60 * 60 * 24 * 7
const LAST_SEEN_LOGOUT_STORAGE_KEY = 'ai-canvas-client:last-seen-shared-logout-at:v1'

let hasRegisteredSharedLogoutSync = false
let sharedCookieSyncPromise: Promise<void> | null = null

interface SharedAuthCookiePayload {
  token: string
  uuid: string
  opTeam: string
}

function canUseDom() {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

function isSharedCookieHost() {
  if (!canUseDom()) {
    return false
  }

  const hostname = window.location.hostname.trim().toLowerCase()
  return hostname === SHARED_COOKIE_ROOT_HOST || hostname.endsWith(`.${SHARED_COOKIE_ROOT_HOST}`)
}

function readCookie(key: string) {
  if (!canUseDom()) {
    return ''
  }

  const cookieValue = `; ${document.cookie}`
  const parts = cookieValue.split(`; ${key}=`)
  const raw = parts.length === 2 ? parts.pop()?.split(';').shift() ?? '' : ''
  return raw ? decodeURIComponent(raw) : ''
}

interface CookieWriteOptions {
  domain?: string
  path?: string
  sameSite?: 'Lax' | 'Strict' | 'None'
  maxAge?: number
}

function writeCookie(key: string, value: string, options: CookieWriteOptions = {}) {
  if (!canUseDom()) {
    return
  }

  const attrs = [
    `${key}=${encodeURIComponent(value)}`,
    `Path=${options.path ?? SHARED_COOKIE_PATH}`,
    `Domain=${options.domain ?? SHARED_COOKIE_DOMAIN}`,
    `SameSite=${options.sameSite ?? 'Lax'}`,
  ]

  if (typeof options.maxAge === 'number') {
    attrs.push(`Max-Age=${options.maxAge}`)
  }

  if (window.location.protocol === 'https:') {
    attrs.push('Secure')
  }

  document.cookie = attrs.join('; ')
}

function deleteCookie(key: string) {
  writeCookie(key, '', {
    maxAge: 0,
  })
}

function readSharedLogoutAt() {
  const value = Number(readCookie(SHARED_LOGOUT_COOKIE_KEY) || 0)
  return Number.isFinite(value) ? value : 0
}

function normalizeCookieString(value: unknown) {
  if (typeof value === 'string') {
    return value.trim()
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }

  return ''
}

function readSharedAuthCookie(): SharedAuthCookiePayload | null {
  const raw = readCookie(SHARED_AUTH_COOKIE_KEY)
  if (!raw) {
    return null
  }

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const token = normalizeCookieString(parsed.token)
    const uuid = normalizeCookieString(parsed.uuid)

    if (!token || !uuid) {
      return null
    }

    return {
      token,
      uuid,
      opTeam: normalizeCookieString(parsed.op_team),
    }
  } catch {
    return null
  }
}

function rememberSharedLogoutAt(value: number) {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.localStorage.setItem(LAST_SEEN_LOGOUT_STORAGE_KEY, String(value || 0))
  } catch {
    /* ignore */
  }
}

function readLastSeenLogoutAt() {
  if (typeof window === 'undefined') {
    return 0
  }

  try {
    const value = Number(window.localStorage.getItem(LAST_SEEN_LOGOUT_STORAGE_KEY) || 0)
    return Number.isFinite(value) ? value : 0
  } catch {
    return 0
  }
}

function hasLocalAuthenticatedSession() {
  const runtimeLoginData = authStore.loginData
  if (authStore.mode === 'authenticated' && runtimeLoginData?.user_info?.token) {
    return true
  }

  return resolveAuthMode(readStoredSession()) === 'authenticated'
}

function readLocalAuthenticatedSession(): AuthApiLoginData | null {
  const runtimeLoginData = authStore.loginData
  if (authStore.mode === 'authenticated' && runtimeLoginData?.user_info?.token) {
    return runtimeLoginData
  }

  const storedLoginData = readStoredSession()
  if (resolveAuthMode(storedLoginData) === 'authenticated') {
    return storedLoginData
  }

  return null
}

function readLocalAuthenticatedUuid() {
  return readLocalAuthenticatedSession()?.user_info?.uuid?.trim() ?? ''
}

function handleSharedLogoutVisibilityChange() {
  if (document.visibilityState === 'visible') {
    syncLogoutStateFromSharedCookie()
  }
}

function handleSharedLogoutStorage(event: StorageEvent) {
  if (event.key !== LAST_SEEN_LOGOUT_STORAGE_KEY || !event.newValue) {
    return
  }

  syncLogoutStateFromSharedCookie()
}

export function runLocalLogout() {
  clearAuthSession('unknown')
}

interface GlobalLogoutOptions {
  redirect?: boolean
  returnTo?: string | null
}

export function runGlobalLogout(options: GlobalLogoutOptions = {}) {
  runLocalLogout()

  if (isSharedCookieHost()) {
    deleteCookie(SHARED_AUTH_COOKIE_KEY)

    const logoutAt = Date.now()
    writeCookie(SHARED_LOGOUT_COOKIE_KEY, String(logoutAt), {
      maxAge: SHARED_LOGOUT_COOKIE_MAX_AGE,
    })
    rememberSharedLogoutAt(logoutAt)
  }

  if (options.redirect !== false) {
    redirectToLogin(options.returnTo)
  }
}

export function syncLogoutStateFromSharedCookie() {
  if (sharedCookieSyncPromise) {
    return
  }

  sharedCookieSyncPromise = (async () => {
    if (!isSharedCookieHost()) {
      return
    }

    const sharedLogoutAt = readSharedLogoutAt()
    const lastSeenLogoutAt = readLastSeenLogoutAt()
    const localAuthenticatedSessionExists = hasLocalAuthenticatedSession()

    if (sharedLogoutAt && sharedLogoutAt > lastSeenLogoutAt) {
      rememberSharedLogoutAt(sharedLogoutAt)

      if (localAuthenticatedSessionExists) {
        runLocalLogout()
      }

      return
    }

    if (!localAuthenticatedSessionExists) {
      return
    }

    const sharedAuth = readSharedAuthCookie()
    if (!sharedAuth) {
      runLocalLogout()
      return
    }

    const localUuid = readLocalAuthenticatedUuid()
    if (localUuid === sharedAuth.uuid) {
      return
    }

    const response = await loginBySharedAuth({
      token: sharedAuth.token,
      uuid: sharedAuth.uuid,
      opTeam: sharedAuth.opTeam,
    })

    if (response.code === 1 && response.data) {
      commitAuthSession(response.data)
      window.location.reload()
      return
    }

    runLocalLogout()
    window.location.reload()
  })().finally(() => {
    sharedCookieSyncPromise = null
  })
}

export function registerSharedLogoutSync() {
  if (!isSharedCookieHost() || hasRegisteredSharedLogoutSync) {
    return
  }

  hasRegisteredSharedLogoutSync = true
  window.addEventListener('storage', handleSharedLogoutStorage)
  window.addEventListener('focus', syncLogoutStateFromSharedCookie)
  document.addEventListener('visibilitychange', handleSharedLogoutVisibilityChange)
}
